export const getAccountName = (account: any) =>
    account?.company_name || account?.company_domain || account?.domain || 'Unnamed account';

export const getAccountDomain = (account: any) => account?.company_domain || account?.domain || '';

export const getAccountKey = (account: any) =>
    account?.recommendation_id ??
    account?.id ??
    account?.account_id ??
    account?.company_domain ??
    account?.domain ??
    account?.company_name;

export const getMatchScore = (account: any) =>
    Math.round(Number(account?.similarity_score ?? account?.combined_score) || 0);

export const getInitials = (name: string) =>
    String(name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase())
        .join('') || '?';

/** Only http(s) links are rendered as links; anything else stays plain text. */
export const getSafeUrl = (value?: string) => {
    if (!value) return null;
    try {
        const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`);
        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
    } catch {
        return null;
    }
};

/** Revenue stored as whole USD millions -> "$949M" / "$69.7B"; empty when unknown or 0. */
export const formatUsdMillions = (value?: number | string | null) => {
    const millions = Number(value)
    if (!Number.isFinite(millions) || millions <= 0) return ''
    if (millions >= 1000) return `$${(millions / 1000).toFixed(1).replace(/\.0$/, '')}B`
    return `$${Math.round(millions).toLocaleString('en-US')}M`
}

export const getMatchVariant = (score: number) =>
    score >= 75 ? 'success' : score >= 50 ? 'warning' : 'neutral';

const REVENUE_UNITS: Array<[RegExp, number]> = [
    [/^(b|bn|billion)\b/, 1e9],
    [/^(m|mn|mm|million)\b/, 1e6],
    [/^(k|thousand)\b/, 1e3],
    [/^(cr|crore|crores)\b/, 1e7],
    [/^(l|lakh|lakhs|lac)\b/, 1e5],
]
// ponytail: fixed INR rate; only used to order rows, never shown. Update if ranking drifts.
const INR_PER_USD = 83

/**
 * Revenue text → approximate USD, for sorting only. Handles "$9.8 Million", "$69.7B",
 * "$1M-$10M" (lower bound) and "₹1-10 Crore (FY2024, Tofler)". Unreadable → null.
 */
export const parseRevenueUsd = (value?: string | number | null): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    const text = String(value ?? '').trim().toLowerCase()
    if (!text) return null
    const match = text.match(/[$₹]\s*(\d[\d,]*(?:\.\d+)?)/) || text.match(/(\d[\d,]*(?:\.\d+)?)/)
    if (!match || match.index === undefined) return null
    const amount = parseFloat(match[1].replace(/,/g, ''))
    if (!Number.isFinite(amount)) return null
    const rest = text.slice(match.index + match[0].length)
    let multiplier = 1
    // The unit is the first unit word after the number ("1-10 crore" → crore).
    for (const piece of rest.split(/[^a-z]+/)) {
        const unit = REVENUE_UNITS.find(([re]) => re.test(piece))
        if (unit) {
            multiplier = unit[1]
            break
        }
    }
    const isInr = /₹|\binr\b|\bcrore|\blakh|\blac\b|\brs\b/.test(text)
    return (amount * multiplier) / (isInr ? INR_PER_USD : 1)
}

/** Headcount text → lower bound ("11-50 employees" → 11, "1,001-5,000" → 1001, "1K+" → 1000). */
export const parseHeadcount = (value?: string | number | null): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    const match = String(value ?? '').toLowerCase().match(/(\d[\d,]*(?:\.\d+)?)\s*(k)?/)
    if (!match) return null
    const n = parseFloat(match[1].replace(/,/g, ''))
    return Number.isFinite(n) ? n * (match[2] ? 1000 : 1) : null
}
